import logger from '../../../utils/logger';
import { DeviceStatus } from '../../../constants/DeviceStatus';
import {
  BatchAction,
  WeighedMaterialType,
} from '../../../constants/MaterialModels';
import { SecondGateOpenDelayUnit } from '../../../constants/DeviceModels';
import { aeratorSwitch } from '../device-switches/aerator-switch';
import { screwSwitch } from '../device-switches/screw-switch';
import type { IBatchStore } from '../batch-store-types';
import { handleDecumWeightTargetReached } from './handle-decum-weight-target-reached';
import { monitorDecumScaleWeight } from './monitor-decum-scale-weight';
import { timerKey } from '../../../utils/timer-manager/timer-manager';

/**
 * Start the decum discharge sequence after delay
 */
export const startDecumDischargeSequence = (
  batchStore: IBatchStore,
  data: any,
  initialData: any,
  scaleRef: any,
) => {
  const { scale, binForDecum, material, materialDetails, mft } = data;
  const { truckCode } = initialData;
  const bin = batchStore.devicesStore.getDeviceById(binForDecum.id) || binForDecum;
  const sid = `${scale.id}-${material.code}`;
  const scaleWeight = batchStore.getScaleWeight(scale);

  const scaleActionType = batchStore.getActionTypeForScale(scale);
  const batchStatusType = batchStore.getBatchStatusTypeForScale(scale);

  if (scaleActionType) {
    logger.log(`scale discharge start: ${scale.type} ${scaleActionType} ${scale.description}`);
    batchStore.onBatchAction(initialData, scaleActionType);
  }

  if (materialDetails.type === WeighedMaterialType.FineAggregate) {
    batchStore.onBatchAction(initialData, BatchAction.FineAggStart);
  }

  if (batchStatusType) {
    logger.log('batch cycle:', batchStatusType);
    batchStore.context.onBatchCycle(batchStatusType, batchStore.inputs, batchStore.outputs, batchStore.channels);
  }

  batchStore.setTargetsForDecumScale(data, initialData, DeviceStatus.DISCHARGING);

  const deviceStatus = batchStore.getDeviceStatus(scale.id);
  let sm = batchStore.getDeviceStatus(sid);

  batchStore.deviceStatus.set(sid, {
    ...sm,
    truckCode,
    status: DeviceStatus.DISCHARGING,
    startWeight: sm.holdWeight ? scaleWeight - sm.holdWeight : scaleWeight,
    finishTarget: sm.holdWeight ? scaleWeight - sm.holdWeight - mft : scaleWeight - mft,
  });

  if (scale.parentScaleId && !sm.holdWeight) {
    const nextScaleStatus = batchStore.getDeviceStatus(scale.parentScaleId);
    batchStore.deviceStatus.set(scale.parentScaleId, {
      ...deviceStatus,
      id: scale.parentScaleId,
      truckCode,
      target: nextScaleStatus.target + data.mft,
      status: DeviceStatus.FEED,
    });
  }

  aeratorSwitch(batchStore.rootStore, scale.aeratorId);
  screwSwitch(batchStore.rootStore, scale.screwId);

  const scalePorts = batchStore.getScalePorts(scale, material);
  const { openPort, closePort, openPortAdditional, closePortAdditional } = scalePorts;
  let freeFall = scaleWeight - (mft - batchStore.getFreeFall(bin, initialData));

  if (sm.holdWeight) {
    freeFall -= sm.holdWeight;
  }

  const noFlowControl = scale.noFlowControl || initialData.sequence?.noFlowControl;
  let initialOpenTime: string | number = '!';
  if (bin.initialOpen && closePort && !noFlowControl) {
    initialOpenTime = bin.initialOpen * 1000;
  }

  batchStore.openScale(scale, initialOpenTime, openPort, closePort);
  batchStore.setWeight('-', scale.inputPNo, 0, freeFall, 0, closePort);

  if (batchStore.isDoubleGateScale(scale)) {
    const secondGateOpenDelay = scale.secondGateOpenDelayUnit === SecondGateOpenDelayUnit.Second
      ? (scale.secondGateOpenDelay || 0) * 1000
      : 0;
    batchStore.timers.clear(timerKey.secondGateOpen(scale.id));
    batchStore.timers.set(timerKey.secondGateOpen(scale.id), () => {
      batchStore.openScale(scale, initialOpenTime, openPortAdditional, closePortAdditional);
      logger.log(`decum scale open second gate ${scale.description} after ${secondGateOpenDelay/1000} sec`);
    }, secondGateOpenDelay, `Decum scale open second gate #${scale.id}`);
  }

  const targetWeight = !closePort ? freeFall : 0;

  batchStore.checkWeightStatus(scale.inputPNo, data, targetWeight, initialData, () => {
    handleDecumWeightTargetReached(batchStore, data, initialData, scaleRef, scalePorts);
  });

  // @ts-ignore
  batchStore.checkDecumScaleWeight(data, initialData, (weight: any, reaction: any) => {
    monitorDecumScaleWeight(batchStore, data, initialData, scaleRef, scalePorts, weight, reaction);
  });
};
